import { Fit, RowStack } from "@skbkontur/react-stack-layout";
import { Button, Input, Modal, Toast } from "@skbkontur/react-ui";
import * as React from "react";

import { ClientInfo } from "../../Domain/ClientInfo/FormsClientInfo";
import { useJsonBin } from "../../Utils/Hooks/UseJsonBin";
import { useLocalStorage } from "../../../../../Common/Hooks/UseLocalStorage";
import { ClientInfoEditor } from "../ClientInfoEditor/ClientInfoEditor";

import { RequisitesSelect } from "./RequisitesSelect";
import { RequisitesSet, RequisitesSetEntry, RequisitesType } from "./RequisitesSet";

export interface IRequisitesProviderContext {
    getRequisitesSetEntries(): Promise<RequisitesSetEntry[]>;
    requestRequisites(buttonText: string): Promise<RequisitesSet | undefined>;
}

export const RequisitesProviderContext = React.createContext<IRequisitesProviderContext | undefined>(undefined);

interface RequisitesProviderProps {
    children: React.ReactNode;
}

export function RequisitesProvider(props: RequisitesProviderProps): JSX.Element {
    const [requisiteList, setRequisiteList] = useJsonBin<{ entries: RequisitesSetEntry[] }>(
        "5f72f9d07243cd7e82462551",
        { entries: [] }
    );

    const [buttonText, setButtonText] = React.useState("OK");
    const [requisitesSetName, setRequisitesSetName] = React.useState("");
    const [requisitesEditor, setRequisitesEditor] = React.useState(false);
    const [requisitesSelector, setRequisitesSelector] = React.useState(false);
    const [requisitesForDraft, setRequisitesForDraft] = useLocalStorage<ClientInfo>("lastFormsClientInfo", {});
    const resolveCallback = React.useRef<undefined | ((result: undefined | RequisitesSet) => void)>(undefined);

    const requisitesProviderContext = React.useMemo<IRequisitesProviderContext>(
        () => ({
            getRequisitesSetEntries: async () => [],
            requestRequisites: async (okButtonText: string): Promise<RequisitesSet | undefined> => {
                setButtonText(okButtonText);
                setRequisitesSetName("");
                startRequisitesSelection();
                return new Promise<undefined | RequisitesSet>(resolve => {
                    resolveCallback.current = (result: undefined | RequisitesSet) => {
                        setRequisitesEditor(false);
                        setRequisitesSelector(false);
                        resolve(result);
                    };
                });
            },
        }),
        [requisiteList]
    );

    const startRequisitesSelection = () => {
        const entries = requisiteList?.entries ?? [];
        if (entries.length === 0) {
            setRequisitesEditor(true);
        } else {
            setRequisitesSelector(true);
        }
    };

    const saveRequisites = () => {
        setRequisiteList({
            entries: [
                ...(requisiteList?.entries ?? []).filter(x => x.name !== requisitesSetName),
                {
                    name: requisitesSetName,
                    value: {
                        type: RequisitesType.FormsClientInfo,
                        content: requisitesForDraft,
                    },
                },
            ],
        });
        Toast.push("Сохранено!");
    };

    return (
        <RequisitesProviderContext.Provider value={requisitesProviderContext}>
            {props.children}
            {requisitesSelector && (
                <Modal ignoreBackgroundClick onClose={() => resolveCallback.current?.(undefined)}>
                    <Modal.Header>Выбрать реквизиты</Modal.Header>
                    <Modal.Body>
                        <div style={{ margin: "0 -35px 0 -30px", width: 600 }}>
                            <RequisitesSelect
                                items={requisiteList.entries ?? []}
                                onSelect={entry => {
                                    resolveCallback.current?.(entry.value);
                                }}
                                onRemove={entry => {
                                    setRequisiteList({
                                        entries: [...(requisiteList?.entries ?? []).filter(x => x !== entry)],
                                    });
                                }}
                                onCreateNew={() => {
                                    setRequisitesSelector(false);
                                    setRequisitesEditor(true);
                                }}
                            />
                        </div>
                    </Modal.Body>
                </Modal>
            )}
            {requisitesEditor && (
                <Modal
                    onClose={() => {
                        resolveCallback.current?.(undefined);
                    }}>
                    <Modal.Header>Пустой черновик с реквизитами</Modal.Header>
                    <Modal.Body>
                        <ClientInfoEditor value={requisitesForDraft} onValueChange={setRequisitesForDraft} />
                    </Modal.Body>
                    <Modal.Footer>
                        <RowStack block gap={2}>
                            <Fit>
                                <Button
                                    onClick={() => {
                                        resolveCallback.current?.({
                                            type: RequisitesType.FormsClientInfo,
                                            content: requisitesForDraft,
                                        });
                                    }}
                                    use={"primary"}>
                                    {buttonText}
                                </Button>
                            </Fit>
                            <Fit>
                                <Button
                                    onClick={() => {
                                        resolveCallback.current?.(undefined);
                                    }}>
                                    Отменить
                                </Button>
                            </Fit>
                            <Fit />
                            <Fit />
                            <Fit>
                                <Input
                                    value={requisitesSetName}
                                    onValueChange={setRequisitesSetName}
                                    placeholder={"Имя для сохранения"}
                                />
                            </Fit>
                            <Fit>
                                <Button
                                    disabled={requisitesSetName === "" || requisitesSetName == undefined}
                                    onClick={saveRequisites}>
                                    Сохранить для всех
                                </Button>
                            </Fit>
                        </RowStack>
                    </Modal.Footer>
                </Modal>
            )}
        </RequisitesProviderContext.Provider>
    );
}
